import { type EvaluationContext, OpenFeature } from '@openfeature/react-sdk';

/**
 * The feature-flag targeting seam (P3 · monorepo-wave-1-p3-feature-flags ·
 * T1/F2, design D3/D8). App wiring (`openfeature-context-sync.tsx`) computes
 * the evaluation context; this function is the ONLY way it reaches the flag
 * mechanism — the reading layer never imports app data layers, and the
 * context flows through here VERBATIM.
 *
 * Contract (each clause is load-bearing):
 *
 * - **Shape-agnostic pass-through.** The app owns the context shape — today
 *   `{ targetingKey, traits: { …flat scalars } }`, the convention the
 *   Flagsmith provider expects: it forwards `traits.*` to identity traits and
 *   DISCARDS unknown top-level attributes. Any "tidying" (flattening,
 *   remapping, cloning-with-transforms) here silently de-targets every user.
 *   This function must never reshape, filter, or merge the object.
 * - **Global is in the name.** This sets the GLOBAL OpenFeature context, so
 *   every registered provider domain inherits it — a domain-scoped provider's
 *   targeting would work *because* of this fan-out. That also
 *   means the traits (email, country, team/org ids) reach every future
 *   registered domain: registering a new provider is a PII decision.
 * - **Deliberately NO domain option.** An advertised-but-ignored `{ domain }`
 *   would read as PII scoping while doing nothing. If domain-scoped context
 *   is ever needed, it arrives as a NEW explicitly-named API that updates
 *   every registered domain deliberately (design open question #3).
 *
 * Home: the door (moved here from `src/features/flags/global-flag-context.ts`
 * at the F-series, doc block intact); consumers are app wiring only
 * (`openfeature-context-sync.tsx`) — never declarations or feature code,
 * which is also why the `@/features/flags` barrel does not re-export it.
 */
export const setGlobalFlagContext = (context: EvaluationContext): Promise<void> =>
  OpenFeature.setContext(context);
